(() => {
  const recommendationSections = new WeakSet();
  const recentlyViewedSections = new WeakSet();
  const storageKey = 'yabanvil-recently-viewed';

  function findSections(scope, selector) {
    const sections = [];
    if (scope instanceof Element && scope.matches(selector)) sections.push(scope);
    scope.querySelectorAll(selector).forEach((section) => sections.push(section));
    return sections;
  }

  async function loadRecommendations(section) {
    if (recommendationSections.has(section)) return;
    recommendationSections.add(section);
    const url = section.dataset.productRecommendationsUrl;
    const sectionId = section.dataset.sectionId;
    if (!url || !sectionId) return;

    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return;
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextSection = documentFragment.querySelector(`[data-product-recommendations][data-section-id="${CSS.escape(sectionId)}"]`);
      const nextContent = nextSection && nextSection.querySelector('[data-product-recommendations-content]');
      const currentContent = section.querySelector('[data-product-recommendations-content]');
      if (nextContent && currentContent) currentContent.replaceWith(nextContent);
    } catch (error) {
      return;
    }
  }

  function safeProductUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return '#';
      return `${url.pathname}${url.search}`;
    } catch (error) {
      return '#';
    }
  }

  function safeImageUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  function createProductImage(source, className, alt) {
    const imageUrl = safeImageUrl(source);
    if (!imageUrl) return null;
    const image = document.createElement('img');
    image.className = className;
    image.src = imageUrl;
    image.alt = alt;
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  }

  function createRecentlyViewedCard(item) {
    const article = document.createElement('article');
    article.className = 'product-card product-card--recent';
    const media = document.createElement('div');
    media.className = 'product-card__media';
    const mediaLink = document.createElement('a');
    mediaLink.className = 'product-card__image-link';
    mediaLink.href = safeProductUrl(item.url);
    mediaLink.setAttribute('aria-label', item.title || 'Product');
    const primary = createProductImage(item.image, 'product-card__image product-card__image--primary', item.title || '');
    const secondary = createProductImage(item.secondaryImage, 'product-card__image product-card__image--secondary', item.title || '');
    if (primary) mediaLink.append(primary);
    else {
      const placeholder = document.createElement('span');
      placeholder.className = 'product-card__image product-recently-viewed__placeholder';
      placeholder.textContent = 'y';
      placeholder.setAttribute('aria-hidden', 'true');
      mediaLink.append(placeholder);
    }
    if (secondary) mediaLink.append(secondary);
    media.append(mediaLink);

    const body = document.createElement('div');
    body.className = 'product-card__body';
    if (item.vendor) {
      const vendor = document.createElement('p');
      vendor.className = 'product-card__vendor';
      vendor.textContent = item.vendor;
      body.append(vendor);
    }
    const heading = document.createElement('h3');
    const titleLink = document.createElement('a');
    titleLink.href = safeProductUrl(item.url);
    titleLink.textContent = item.title || 'Product';
    heading.append(titleLink);
    const price = document.createElement('div');
    price.className = 'product-card__price';
    const priceValue = document.createElement('strong');
    priceValue.textContent = item.price || '';
    price.append(priceValue);
    body.append(heading, price);
    article.append(media, body);
    return article;
  }

  function readRecentlyViewed() {
    try {
      const value = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  }

  function writeRecentlyViewed(products) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(products.slice(0, 12)));
    } catch (error) {
      return;
    }
  }

  async function initializeRecentlyViewed(section) {
    if (recentlyViewedSections.has(section)) return;
    recentlyViewedSections.add(section);
    const dataNode = section.querySelector('[data-recent-current-product]');
    const grid = section.querySelector('[data-recent-products-grid]');
    if (!dataNode || !grid) return;

    let currentProduct;
    try {
      currentProduct = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }
    const stored = readRecentlyViewed();
    const previousProducts = stored.filter((item) => String(item.id) !== String(currentProduct.id));
    const limit = Number(section.dataset.limit) || 4;
    const freshProducts = await Promise.all(previousProducts.slice(0, limit).map(async (item) => {
      try {
        const url = new URL(safeProductUrl(item.url), window.location.origin);
        if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return null;
        url.search = ''; url.hash = ''; url.pathname += '.js';
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return null;
        const product = await response.json();
        const price = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: section.dataset.currency }).format(product.price / 100);
        return { ...item, title: product.title, price, image: product.featured_image, secondaryImage: product.images[1] || '', vendor: product.vendor };
      } catch (error) { return null; }
    }));
    freshProducts.filter(Boolean).forEach(item => grid.append(createRecentlyViewedCard(item)));
    if (grid.children.length > 0) section.hidden = false;
    writeRecentlyViewed([currentProduct, ...previousProducts]);
  }

  function initializeProductSections(scope = document) {
    findSections(scope, '[data-product-recommendations]').forEach(loadRecommendations);
    findSections(scope, '[data-recently-viewed-products]').forEach(initializeRecentlyViewed);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeProductSections());
  else initializeProductSections();
  document.addEventListener('shopify:section:load', (event) => initializeProductSections(event.target));
})();
